import { ThemeProvider } from '@volkswagen-onehub/components-core';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ErrorScreen } from './components/screen/error';
import { LoadingScreen } from './components/screen/loading';
import { ThankyouScreen } from './components/screen/thankyou';
import { FIELD_NAME } from './constants/form-constants';
import { AppProvider, useApp } from './contexts/app-context';
import { formService } from './form-service';
import { TheOriginalsFest2026Form } from './forms/adhoc/theoriginals-2026/theoriginalsfest-form';
import { LeadVentaController } from './forms/lead-venta/lead-venta-controller';
import { TestForm } from './forms/test/test-form';
import { getBrand } from './helpers/carline-helpers';
import {
	getFormName,
	getPageCategory,
	hasOfertaFromExternalContent,
	isCallMeForm,
	isInvertedThemeForm,
} from './helpers/form-helpers';
import { configProviderManager } from './service/config-provider';
import { externalContentManager } from './service/external-content';
import { styles } from './styles';
import { useTrackingManager } from './tracking/use-tracking-manager';
import { useFeatureServices } from './use-feature-services';

export function App(): JSX.Element {
	const {
		's2:history': historyService,
		's2:logger': logger,
		'service-config-provider': serviceConfigProvider,
		'fa-content-service': faContentService,
	} = useFeatureServices();

	useEffect(() => {
		console.log('🚀 ~ App ~ logger', logger);
		console.log('🚀 ~ App ~ historyService', historyService);
		console.log('🚀 ~ App ~ serviceConfigProvider', serviceConfigProvider);
		console.log('🚀 ~ App ~ faContentService', faContentService?.content);
	}, [logger, historyService, serviceConfigProvider, faContentService]);

	const trackingManager = useTrackingManager();

	useEffect(() => {
		if (faContentService?.content) {
			if (!(faContentService.content as any).params) {
				(faContentService.content as any).params = {};
			}

			(faContentService.content as any).params.onCloseCallback = () => {
				trackingManager.trackFormCloseClick({ contentId: 'Cerrar' });
			};

			if (isCallMeForm()) {
				// El tracking de carga de call me lo lanza CallMeForm solo si está abierto
			} else if (hasOfertaFromExternalContent()) {
				// Tracking específico para lead venta oferta que espera VEHICLE_PRECONFIGURATION
				let tracked = false;
				const tracking = () => {
					if (tracked) return;
					const vehiclePreconfiguration = formService.getFieldValue(FIELD_NAME.VEHICLE_PRECONFIGURATION);
					if (vehiclePreconfiguration !== undefined) {
						trackingManager.trackFormLayerLoad(getFormName(), getBrand(), getPageCategory(), vehiclePreconfiguration);
						tracked = true;
					}
				};
				tracking();

				const unsubscribe = formService.subscribeToField(FIELD_NAME.VEHICLE_PRECONFIGURATION, { onChange: tracking });
				return () => unsubscribe();
			} else {
				// Tracking normal para otros formularios
				trackingManager.trackFormLayerLoad(getFormName(), getBrand(), getPageCategory());
			}
		}
		return () => {};
	}, [faContentService]);

	return (
		<AppProvider>
			<AppContent serviceConfigProvider={serviceConfigProvider} faContentService={faContentService} />
		</AppProvider>
	);
}

interface AppContentProps {
	serviceConfigProvider: any;
	faContentService: any;
}

const AppContent = ({ serviceConfigProvider, faContentService }: AppContentProps) => {
	const { loading, error } = useApp();
	const [isAppReady, setIsAppReady] = useState(false);

	useLayoutEffect(() => {
		configProviderManager.setConfigs(serviceConfigProvider?.configs as any);
		externalContentManager.setFaContent(faContentService?.content as any);
		formService.reset();
		setIsAppReady(true);
	}, [serviceConfigProvider, faContentService]);

	return (
		<div className="oneform">
			<ThemeProvider theme={isInvertedThemeForm() ? 'inverted' : 'main'}>
				<style>{styles}</style>
				{isAppReady ? (
					<>
						<Form />
						{/* Los errores los muestra Form */}
						{loading && !error && <LoadingScreen />}
					</>
				) : (
					<LoadingScreen />
				)}
			</ThemeProvider>
		</div>
	);
};

const Form = () => {
	const { error, success, thankyouScreen } = useApp();

	if (error) return <ErrorScreen />;
	if (success) return thankyouScreen || <ThankyouScreen />;

	const formName = externalContentManager.formName;

	switch (formName) {
		case 'adhoc-theoriginalsfest-2026':
			return <TheOriginalsFest2026Form />;
		case 'test':
			return <TestForm />;
		default:
			return <LeadVentaController />;
	}
};
